import { FilterClearButton, StatusBadge } from "@components";
import { Button as UntitledButton } from "@components/button/button-untitledui";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { getLocalTimeZone } from "@internationalized/date";
import { Download01 } from "@untitledui/icons";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import type { DateValue } from "react-aria-components";
import { TableCustom } from "@/components/ui/table";
import { useToast } from "@/providers/toast-provider";
import {
	vistoriasBuscar,
	vistoriasDataFiltrar,
	vistoriasExportar,
} from "@/services/analytics/events";
import { useDebouncedCommit } from "@/shared/hooks/use-debounced-commit";
import { InspectionsExportModal } from "../components/inspections-export-modal";
import { useGetInspections } from "../hooks";
import { getInspectionPdfUrl } from "../services";
import type { InspectionItem } from "../types/inspections";
import {
	canGetPdf,
	getInspectionStatusBadgePalette,
	mapStatus,
} from "../utils/inspectionUtils";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;
const trimSearch = (value: string) => value.trim();

const InspectionsPageList = () => {
	const { showToast } = useToast();
	const [currentPage, setCurrentPage] = useState(1);
	const [searchInput, setSearchInput] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedDate, setSelectedDate] = useState<DateValue | null>(null);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState(false);

	const hasActiveFilters =
		searchInput.trim().length > 0 || selectedDate !== null;

	const submittedAt = selectedDate
		? dayjs(selectedDate.toDate(getLocalTimeZone())).format("YYYY-MM-DD")
		: undefined;

	const inspectionsQuery = useGetInspections({
		PageNumber: currentPage,
		PageSize: PAGE_SIZE,
		Search: searchTerm,
		SubmittedAt: submittedAt,
	});
	const {
		data: inspectionsData,
		isFetching,
		isError,
		refetch,
	} = inspectionsQuery;

	const { total: totalPages } = usePaginationTotal({
		page: currentPage,
		onPageChange: setCurrentPage,
		criteria: { search: searchTerm, submittedAt, pageSize: PAGE_SIZE },
		totalPages: inspectionsData?.totalPages,
		query: inspectionsQuery,
	});

	const data = useMemo(
		() =>
			(inspectionsData?.items || []).map((item: InspectionItem) => ({
				...item,
				fileStatus: mapStatus(item.fileStatus),
			})),
		[inspectionsData]
	);

	const columns = [
		{
			id: "createdAt",
			label: "Data e hora da vistoria",
			render: (item: InspectionItem) =>
				dayjs(item.createdAt).format("DD/MM/YYYY HH:mm"),
		},
		{ id: "checklist", label: "Formulário" },
		{ id: "client", label: "Cliente" },
		{ id: "inspector", label: "Vistoriador" },
		{ id: "plate", label: "Placa" },
		{
			id: "fileStatus",
			label: "Situação",
			render: (item: InspectionItem) =>
				item.fileStatus ? (
					<StatusBadge
						label={item.fileStatus}
						palette={getInspectionStatusBadgePalette(item.fileStatus)}
					/>
				) : null,
		},
	];

	const handleDownloadPdf = async (item: InspectionItem) => {
		try {
			if (!item.id || item.id <= 0) {
				throw new Error("ID inválido");
			}

			setIsDownloading(true);

			const pdfUrl = await getInspectionPdfUrl(item.id);
			if (!pdfUrl) {
				throw new Error("PDF não pronto");
			}

			vistoriasExportar(item.id);
			window.open(pdfUrl, "_blank", "noopener,noreferrer");
		} catch {
			showToast({
				title: "PDF não disponível",
				description: "Aguarde processamento",
				variant: "error",
			});
		} finally {
			setIsDownloading(false);
		}
	};

	const handleClearFilters = () => {
		setSearchInput("");
		setSearchTerm("");
		setSelectedDate(null);
		setCurrentPage(1);
	};

	const actions = [
		{
			icon: Download01,
			label: (item: InspectionItem) => {
				if (!canGetPdf(item)) {
					return "Processando...";
				}
				if (isDownloading) {
					return "Baixando...";
				}
				return "Baixar PDF";
			},

			onClick: handleDownloadPdf,
			disabled: (item: InspectionItem) => !canGetPdf(item) || isDownloading,
		},
	];

	useDebouncedCommit({
		value: searchInput,
		applied: searchTerm,
		delay: SEARCH_DEBOUNCE_MS,
		normalize: trimSearch,
		onCommit: (term) => {
			setSearchTerm(term);
			setCurrentPage(1);
			if (term) {
				vistoriasBuscar(term);
			}
		},
	});

	return (
		<>
			<TableCustom
				actions={actions}
				addButtonLabel="Novo formulário"
				columns={columns}
				currentPage={currentPage}
				data={data}
				errorMessage="Não foi possível carregar as vistorias."
				extraActions={
					<>
						{hasActiveFilters ? (
							<FilterClearButton onClick={handleClearFilters} />
						) : null}
						<UntitledButton
							aria-label="Exportar vistorias"
							color="secondary"
							iconLeading={Download01}
							onClick={() => setIsExportModalOpen(true)}
							size="sm"
							type="button"
						>
							Exportar
						</UntitledButton>
					</>
				}
				filterBarLabel="Filtros de vistorias"
				isError={isError}
				isLoading={isFetching}
				layout="page"
				onDateChange={(date) => {
					setSelectedDate(date);
					setCurrentPage(1);
					if (date) {
						const dateStr = dayjs(date.toDate(getLocalTimeZone())).format(
							"YYYY-MM-DD"
						);
						vistoriasDataFiltrar(dateStr);
					}
				}}
				onPageChange={setCurrentPage}
				onRetry={() => {
					refetch();
				}}
				onSearchChange={(value) => {
					setSearchInput(value);
					setCurrentPage(1);
				}}
				searchPlaceholder="Pesquise por placa ou vistoriador..."
				searchValue={searchInput}
				selectedDate={selectedDate}
				title="Histórico de vistorias"
				totalPages={totalPages}
			/>

			<InspectionsExportModal
				isOpen={isExportModalOpen}
				onClose={() => setIsExportModalOpen(false)}
			/>
		</>
	);
};

export default InspectionsPageList;
